import React, { useState, useEffect } from 'react';
import { InvoiceSettings, InvoiceSettingsService, defaultInvoiceSettings } from '../services/invoiceSettingsService';
import { Customer } from '../lib/customers';
import { supabase } from '../lib/supabase';

// Stamp components
const PaidStamp = () => (
  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[-30deg] border-[3px] border-green-600 rounded-lg px-6 sm:px-8 py-3 sm:py-4 opacity-30">
    <span className="text-xl sm:text-3xl font-bold text-green-600">PAID</span>
  </div>
);

// Types
interface ProductSaleItem {
  name: string;
  quantity: number;
  price: number;
}

interface BusinessInfo {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  gst_number?: string;
  pst_number?: string;
}

interface ProductSaleInfo {
  saleNumber: string;
  saleData: {
    subtotal: number;
    subtotal_after_discounts?: number;
    taxAmount: number;
    total: number;
    total_discounts?: number;
    discounts?: Array<{
      id: string;
      title: string;
      type: 'percentage' | 'fixed';
      amount: number;
    }>;
    items: ProductSaleItem[];
  };
  paymentMethod: string;
  paymentStatus?: 'paid' | 'unpaid' | 'partial';
  amountPaid?: number;
  saleDate: string;
}

interface StyledInvoiceDisplayProps {
  sale: ProductSaleInfo;
  business: BusinessInfo;
  customer?: Customer | null;
  className?: string;
  forwardRef?: React.RefObject<HTMLDivElement>;
}

export default function StyledInvoiceDisplay({ 
  sale, 
  business, 
  customer, 
  className = "", 
  forwardRef 
}: StyledInvoiceDisplayProps) {
  const [invoiceSettings, setInvoiceSettings] = useState<InvoiceSettings | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [taxLabel, setTaxLabel] = useState('Tax');

  // Load invoice settings
  useEffect(() => {
    const fetchInvoiceSettings = async () => {
      if (!business?.id) return;
      
      setIsLoadingSettings(true);
      try {
        const settings = await InvoiceSettingsService.getInvoiceSettings(business.id);
        if (settings) {
          setInvoiceSettings(settings);
        } else {
          // No settings found, create default settings with business info
          const defaultSettings: InvoiceSettings = {
            business_id: business.id,
            company_name: business.name || 'Your Business Name',
            company_address: business.address || '123 Business Street\nCity, State 12345',
            company_phone: business.phone || '(555) 123-4567',
            company_email: business.email || 'info@yourbusiness.com',
            company_website: `www.${business.name?.toLowerCase().replace(/\s+/g, '')}.com` || 'www.yourbusiness.com',
            ...defaultInvoiceSettings
          } as InvoiceSettings;
          setInvoiceSettings(defaultSettings);
        }
      } catch (error) {
        console.error('Error fetching invoice settings:', error);
        // Fall back to defaults
        const fallbackSettings: InvoiceSettings = {
          business_id: business.id,
          company_name: business.name || 'Your Business Name',
          company_address: business.address || '123 Business Street\nCity, State 12345',
          company_phone: business.phone || '(555) 123-4567',
          company_email: business.email || 'info@yourbusiness.com',
          company_website: `www.${business.name?.toLowerCase().replace(/\s+/g, '')}.com` || 'www.yourbusiness.com',
          ...defaultInvoiceSettings
        } as InvoiceSettings;
        setInvoiceSettings(fallbackSettings);
      } finally {
        setIsLoadingSettings(false);
      }
    };

    fetchInvoiceSettings();
  }, [business?.id]);

  useEffect(() => {
    if (!business?.id) return;
    supabase
      .from('tax_rates')
      .select('name, rate')
      .eq('business_id', business.id)
      .then(({ data }) => {
        if (data && data.length > 0) {
          setTaxLabel(data.map(r => `${r.name} (${r.rate}%)`).join(' + '));
        }
      });
  }, [business?.id]);

  if (isLoadingSettings) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-gray-700 font-medium text-lg">Loading invoice settings...</p>
        </div>
      </div>
    );
  }

  if (!invoiceSettings) {
    return <div className="text-center py-8 text-gray-500">Unable to load invoice settings</div>;
  }

  return (
    <div ref={forwardRef} className={`bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 relative ${className}`} style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {sale.paymentStatus === 'paid' && <PaidStamp />}
      
      {/* Invoice Header */}
      <div className="p-4 sm:p-6 lg:p-8" style={{ backgroundColor: invoiceSettings.header_background || '#F8FAFC' }}>
        <div className="flex flex-col sm:flex-row justify-between items-start space-y-4 sm:space-y-0">
          <div className="w-full sm:w-auto">
            {invoiceSettings.include_logo && invoiceSettings.logo_url && (
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-200 rounded-lg mb-3 sm:mb-4 flex items-center justify-center text-gray-400 text-xs overflow-hidden">
                <img 
                  src={invoiceSettings.logo_url} 
                  alt="Company Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: invoiceSettings.primary_color || '#3B82F6' }}>
              RECEIPT
            </h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">#{sale.saleNumber}</p>
          </div>
          <div className="text-left sm:text-right w-full sm:w-auto">
            <h2 className="text-lg sm:text-xl font-semibold" style={{ color: invoiceSettings.text_color || '#374151' }}>
              {invoiceSettings.company_name || 'Your Business Name'}
            </h2>
            <div className="text-xs sm:text-sm text-gray-600 mt-2 whitespace-pre-line">
              {invoiceSettings.company_address || '123 Business Street\nCity, State 12345'}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 mt-2 space-y-1">
              <div>{invoiceSettings.company_phone || '(555) 123-4567'}</div>
              <div className="break-all">{invoiceSettings.company_email || 'info@yourbusiness.com'}</div>
              <div className="break-all">{invoiceSettings.company_website || 'www.yourbusiness.com'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Details */}
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-4 sm:mb-6 lg:mb-8">
          {/* Bill To */}
          {customer && (
            <div className="bg-gray-50 sm:bg-transparent rounded-lg sm:rounded-none p-3 sm:p-0">
              <h3 className="font-semibold text-sm sm:text-base lg:text-lg mb-2 text-gray-800" style={{ color: invoiceSettings.text_color || '#374151' }}>Bill To:</h3>
              <div className="text-gray-600 text-sm sm:text-base space-y-1">
                <div className="font-semibold text-gray-900">
                  {customer.first_name} {customer.last_name}
                </div>
                {customer.email && (
                  <div className="break-all text-xs sm:text-sm">{customer.email}</div>
                )}
                {customer.phone && (
                  <div className="text-xs sm:text-sm">{customer.phone}</div>
                )}
              </div>
            </div>
          )}
          
          {/* Sale Info */}
          <div className="bg-gray-50 sm:bg-transparent rounded-lg sm:rounded-none p-3 sm:p-0 text-left sm:text-right">
            <div className="space-y-2 text-xs sm:text-sm lg:text-base">
              <div className="flex justify-between sm:justify-end sm:space-x-4">
                <span className="font-medium text-gray-700">Sale Date:</span>
                <span className="font-semibold">{new Date(sale.saleDate).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between sm:justify-end sm:space-x-4">
                <span className="font-medium text-gray-700">Payment Method:</span>
                <span className="font-semibold">{sale.paymentMethod}</span>
              </div>
            </div>
          </div>
        </div>
            
        {/* Products Table */}
        <div className="border border-gray-200 rounded-lg overflow-hidden mb-6 sm:mb-8">
          {/* Desktop Table Header */}
          <div className="hidden sm:block bg-gray-50 p-4">
            <div className="grid gap-2 font-semibold text-gray-700" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
              <div>Product</div>
              <div className="text-center">Quantity</div>
              <div className="text-right">Rate</div>
              <div className="text-right">Amount</div>
            </div>
          </div>
          
          {/* Products */}
          <div className="divide-y divide-gray-200">
            {sale.saleData.items.map((item, index) => (
              <div key={index} className="p-4">
                {/* Desktop Layout */}
                <div className="hidden sm:grid gap-2 items-center" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
                  <div>
                    <div className="font-medium text-gray-900">{item.name}</div>
                  </div>
                  <div className="text-center text-gray-600">{item.quantity}</div>
                  <div className="text-right text-gray-600">${item.price.toFixed(2)}</div>
                  <div className="text-right font-medium text-gray-900">${(item.price * item.quantity).toFixed(2)}</div>
                </div>
                
                {/* Mobile Layout */}
                <div className="sm:hidden space-y-2">
                  <div className="flex justify-between items-start">
                    <div className="font-medium text-gray-900 flex-1">{item.name}</div>
                    <div className="font-medium text-gray-900 ml-4">${(item.price * item.quantity).toFixed(2)}</div>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Qty: {item.quantity} × ${item.price.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-full sm:w-80 space-y-2">
            <div className="flex justify-between text-sm text-gray-600 pb-2">
              <span>Subtotal</span>
              <span>${sale.saleData.subtotal.toFixed(2)}</span>
            </div>
            
            {/* Discounts */}
            {sale.saleData.discounts && sale.saleData.discounts.length > 0 && (
              <>
                {sale.saleData.discounts.map((discount) => (
                  <div key={discount.id} className="flex justify-between text-sm text-red-600 pb-2">
                    <span>
                      {discount.title} 
                      {discount.type === 'percentage' ? ` (${discount.amount}%)` : ''}
                    </span>
                    <span>
                      -${discount.type === 'percentage' 
                        ? ((sale.saleData.subtotal * discount.amount) / 100).toFixed(2)
                        : discount.amount.toFixed(2)
                      }
                    </span>
                  </div>
                ))}
                {sale.saleData.subtotal_after_discounts !== undefined && (
                  <div className="flex justify-between text-sm font-medium text-gray-700 pb-2 border-t border-gray-100 pt-2">
                    <span>Subtotal after discounts</span>
                    <span>${sale.saleData.subtotal_after_discounts.toFixed(2)}</span>
                  </div>
                )}
              </>
            )}
            
            {invoiceSettings.show_tax_breakdown && (
              <div className="flex justify-between text-sm text-gray-600 pb-2">
                <span>{taxLabel}</span>
                <span>${sale.saleData.taxAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-200" style={{ color: invoiceSettings.accent_color || '#059669' }}>
              <span>Total</span>
              <span>${sale.saleData.total.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-green-600 pt-1">
              <span>Amount Paid</span>
              <span>${(() => {
                if (sale.paymentStatus === 'paid') return sale.saleData.total.toFixed(2);
                if (sale.paymentStatus === 'partial') return (sale.amountPaid || 0).toFixed(2);
                return '0.00'; // unpaid or undefined
              })()}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Amount Due</span>
              <span>${(() => {
                if (sale.paymentStatus === 'paid') return '0.00';
                if (sale.paymentStatus === 'partial') return (sale.saleData.total - (sale.amountPaid || 0)).toFixed(2);
                return sale.saleData.total.toFixed(2); // unpaid or undefined
              })()}</span>
            </div>
          </div>
        </div>

        {/* Thank You Message */}
        {invoiceSettings.thank_you_message && (
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-lg font-medium" style={{ color: invoiceSettings.accent_color || '#059669' }}>
              {invoiceSettings.thank_you_message}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
